import { financialTone } from "./financialTone";

const unavailable = "Unavailable";

function formatStatus(value) {
  return String(value || unavailable)
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function statusTone(value) {
  const normalized = String(value || "").toUpperCase();
  if (normalized === "RUNNING") return "online";
  if (normalized === "STARTING" || normalized === "DEGRADED") {
    return "connecting";
  }
  return "offline";
}

// `null`/`undefined` mean the figure was not published this cycle — e.g. MT5
// disconnected — and must read as unavailable rather than as a real zero.
// Number(null) is 0, which is why a bare Number() conversion can't be used
// here the way it safely can for a value that is never optional.
function toFiniteNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
}

function formatCurrency(value, { signed = false } = {}) {
  const amount = toFiniteNumber(value);
  if (amount === null) return unavailable;

  const formatted = Math.abs(amount).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const sign = signed ? (amount >= 0 ? "+" : "-") : "";
  return `${sign}$${formatted}`;
}

function formatHeartbeat(value, now) {
  const heartbeat = Date.parse(value);
  if (!Number.isFinite(heartbeat)) return unavailable;

  const elapsedSeconds = Math.max(
    0,
    Math.floor((now.getTime() - heartbeat) / 1000)
  );
  if (elapsedSeconds < 60) return `${elapsedSeconds} sec ago`;
  if (elapsedSeconds < 3600) {
    return `${Math.floor(elapsedSeconds / 60)} min ago`;
  }
  return `${Math.floor(elapsedSeconds / 3600)} hr ago`;
}

export function buildLiveOverviewData(status, now = new Date()) {
  const botTone = statusTone(status.goldbot_status);
  const floatingPl = toFiniteNumber(status.floating_pl);
  const marginLevel = toFiniteNumber(status.margin_level);
  const accountNumber = String(status.account_number || "");

  return {
    systemStatus: [
      {
        label: "GoldBot Status",
        value: formatStatus(status.goldbot_status),
        tone: botTone,
        status: botTone,
      },
      {
        label: "MT5 Connected",
        value:
          status.mt5_connected === true
            ? "Connected"
            : status.mt5_connected === false
            ? "Disconnected"
            : "Unknown",
        tone:
          status.mt5_connected === true
            ? "online"
            : status.mt5_connected === false
            ? "offline"
            : "connecting",
        status:
          status.mt5_connected === true
            ? "online"
            : status.mt5_connected === false
            ? "offline"
            : "connecting",
      },
      {
        label: "VPS Uptime",
        value: status.vps_uptime || unavailable,
        tone: "online",
      },
      {
        label: "GoldBot Uptime",
        value: status.goldbot_uptime || unavailable,
        tone: "online",
      },
      status.stale
        ? {
            label: "Last Heartbeat",
            value: "Stale Snapshot",
            tone: "connecting",
            status: "connecting",
          }
        : {
            label: "Last Heartbeat",
            value: formatHeartbeat(status.last_heartbeat, now),
            tone: "pulse",
          },
    ],
    accountMetrics: [
      {
        label: "Account Name",
        value: status.account_name || unavailable,
      },
      {
        label: "Account Number",
        value: accountNumber
          ? `•••• ${accountNumber.slice(-4)}`
          : unavailable,
      },
      { label: "Broker", value: status.broker || unavailable },
      { label: "Balance", value: formatCurrency(status.balance) },
      {
        label: "Equity",
        value: formatCurrency(status.equity),
        tone: "positive",
      },
      {
        label: "Floating P/L",
        value: formatCurrency(floatingPl, { signed: true }),
        // Directional: a floating loss is red, and an account sitting
        // exactly flat is neither.
        tone: financialTone(floatingPl),
      },
      { label: "Free Margin", value: formatCurrency(status.free_margin) },
      {
        label: "Margin Level",
        value:
          marginLevel !== null
            ? `${marginLevel.toLocaleString("en-US", {
                maximumFractionDigits: 1,
              })}%`
            : unavailable,
        tone: "positive",
      },
    ],
    tradingMetrics: [
      {
        label: "Open Trades",
        value: String(status.open_trades),
        detail: "Live snapshot",
      },
      {
        label: "Active Strategies",
        value: String(status.active_strategies),
        detail: "Registered and active",
      },
    ],
  };
}

/**
 * The shape the Overview renders when the API has told us nothing yet, or has
 * stopped answering. Every slot is explicitly unavailable so an offline API
 * can never look like a healthy account.
 */
export function buildUnavailableOverviewData() {
  return {
    systemStatus: [
      {
        label: "GoldBot Status",
        value: unavailable,
        tone: "offline",
        status: "offline",
      },
      {
        label: "MT5 Connected",
        value: unavailable,
        tone: "offline",
        status: "offline",
      },
      { label: "VPS Uptime", value: unavailable, tone: "offline" },
      { label: "GoldBot Uptime", value: unavailable, tone: "offline" },
      { label: "Last Heartbeat", value: unavailable, tone: "offline" },
    ],
    accountMetrics: [
      { label: "Account Name", value: unavailable },
      { label: "Account Number", value: unavailable },
      { label: "Broker", value: unavailable },
      { label: "Balance", value: unavailable },
      { label: "Equity", value: unavailable },
      { label: "Floating P/L", value: unavailable },
      { label: "Free Margin", value: unavailable },
      { label: "Margin Level", value: unavailable },
    ],
    tradingMetrics: [
      { label: "Open Trades", value: unavailable, detail: "No live snapshot" },
      {
        label: "Active Strategies",
        value: unavailable,
        detail: "No live snapshot",
      },
    ],
  };
}

export function applyOverviewRequestState(items, { error, isLoading }) {
  if (!error && !isLoading) return items;

  const [goldBotStatus, ...remainingItems] = items;
  return [
    {
      ...goldBotStatus,
      value: error ? "GoldBot Offline" : "Loading...",
      tone: error ? "offline" : "connecting",
      status: error ? "offline" : "connecting",
    },
    ...remainingItems,
  ];
}
